import {
  VESSEL_SEARCHER_DEFAULT_FILTERS,
  VESSEL_SEARCHER_FILTER_BOUNDS,
  VESSEL_SEARCHER_PAGE_SIZE,
} from "../constants/vessel-searcher.constants";
import { getMockVesselSearcherResponse } from "../mocks/vessel-searcher.mocks";
import type {
  VesselSearcherFilterBounds,
  VesselSearcherFilters,
  VesselSearcherResponse,
  VesselSearcherRangeFilterKey,
  VesselSearcherSortDirection,
  VesselSearcherSortKey,
} from "../models/vessel-searcher.models";
import { getVesselSearcherVessels } from "../services/vessel-searcher.service";
import { vesselSearcherMockEnabledAtom } from "../state/vessel-searcher.atoms";
import { getVesselSearcherErrorMessage } from "../utils/vessel-searcher-format.utils";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

const toEffectiveBounds = (bounds: VesselSearcherFilterBounds | undefined): VesselSearcherFilterBounds => ({
  buildDate: {
    min: bounds?.buildDate.min ?? VESSEL_SEARCHER_FILTER_BOUNDS.buildDate.min,
    max: bounds?.buildDate.max ?? VESSEL_SEARCHER_FILTER_BOUNDS.buildDate.max,
  },
  dwt: {
    min: bounds?.dwt.min ?? VESSEL_SEARCHER_FILTER_BOUNDS.dwt.min,
    max: bounds?.dwt.max ?? VESSEL_SEARCHER_FILTER_BOUNDS.dwt.max,
  },
  lbp: {
    min: bounds?.lbp.min ?? VESSEL_SEARCHER_FILTER_BOUNDS.lbp.min,
    max: bounds?.lbp.max ?? VESSEL_SEARCHER_FILTER_BOUNDS.lbp.max,
  },
  draft: {
    min: bounds?.draft.min ?? VESSEL_SEARCHER_FILTER_BOUNDS.draft.min,
    max: bounds?.draft.max ?? VESSEL_SEARCHER_FILTER_BOUNDS.draft.max,
  },
  depth: {
    min: bounds?.depth.min ?? VESSEL_SEARCHER_FILTER_BOUNDS.depth.min,
    max: bounds?.depth.max ?? VESSEL_SEARCHER_FILTER_BOUNDS.depth.max,
  },
  breadth: {
    min: bounds?.breadth.min ?? VESSEL_SEARCHER_FILTER_BOUNDS.breadth.min,
    max: bounds?.breadth.max ?? VESSEL_SEARCHER_FILTER_BOUNDS.breadth.max,
  },
});

const filtersFromBounds = (bounds: VesselSearcherFilterBounds): VesselSearcherFilters => ({
  ...VESSEL_SEARCHER_DEFAULT_FILTERS,
  buildDateFrom: bounds.buildDate.min ?? "",
  buildDateTo: bounds.buildDate.max ?? "",
  dwt: { min: bounds.dwt.min ?? "", max: bounds.dwt.max ?? "" },
  lbp: { min: bounds.lbp.min ?? "", max: bounds.lbp.max ?? "" },
  draft: { min: bounds.draft.min ?? "", max: bounds.draft.max ?? "" },
  depth: { min: bounds.depth.min ?? "", max: bounds.depth.max ?? "" },
  breadth: { min: bounds.breadth.min ?? "", max: bounds.breadth.max ?? "" },
});

const changedRangeFilters = (
  current: VesselSearcherFilters,
  next: VesselSearcherFilters,
): VesselSearcherRangeFilterKey[] => {
  const changed: VesselSearcherRangeFilterKey[] = [];

  if (current.buildDateFrom !== next.buildDateFrom || current.buildDateTo !== next.buildDateTo) {
    changed.push("buildDate");
  }

  (["dwt", "lbp", "draft", "depth", "breadth"] as const).forEach((key) => {
    if (current[key].min !== next[key].min || current[key].max !== next[key].max) {
      changed.push(key);
    }
  });

  return changed;
};

// Owns the dashboard request lifecycle so routing and presentation stay free of source-selection behavior.
// The first real request is intentionally unconstrained, then Snowflake metadata fills the visible full-range filters.
export const useVesselSearcher = () => {
  const useMockData = useAtomValue(vesselSearcherMockEnabledAtom);
  const [filters, setFiltersState] = useState<VesselSearcherFilters>(VESSEL_SEARCHER_DEFAULT_FILTERS);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<VesselSearcherSortKey>("dwt");
  const [sortDirection, setSortDirection] = useState<VesselSearcherSortDirection>("desc");
  const [activeRangeFilters, setActiveRangeFilters] = useState<VesselSearcherRangeFilterKey[]>([]);
  const [response, setResponse] = useState<VesselSearcherResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializedSource = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const sourceKey = useMockData ? "mock" : "snowflake";
    const delay = useMockData ? 0 : 250;

    if (initializedSource.current !== null && initializedSource.current !== sourceKey) {
      initializedSource.current = null;
      setFiltersState(VESSEL_SEARCHER_DEFAULT_FILTERS);
      setOffset(0);
      setActiveRangeFilters([]);
      setResponse(null);
      setIsLoading(true);
      setError(null);

      return () => controller.abort();
    }

    setIsLoading(true);
    setError(null);

    const timer = window.setTimeout(() => {
      const request = {
        ...filters,
        activeRangeFilters,
        sortBy,
        sortDirection,
        offset,
        limit: VESSEL_SEARCHER_PAGE_SIZE,
      };
      const requestPromise = useMockData
        ? Promise.resolve(getMockVesselSearcherResponse(request))
        : getVesselSearcherVessels(request, controller.signal);

      requestPromise
        .then((nextResponse) => {
          setResponse(nextResponse);

          if (initializedSource.current !== sourceKey) {
            initializedSource.current = sourceKey;
            setFiltersState((current) => ({
              ...filtersFromBounds(toEffectiveBounds(nextResponse.filterBounds)),
              imo: current.imo,
              vesselName: current.vesselName,
              useScrubber: current.useScrubber,
              hasVoyageInImos: current.hasVoyageInImos,
              engineTypes: current.engineTypes,
              designs: current.designs,
              yards: current.yards,
              countries: current.countries,
            }));
          }
        })
        .catch((requestError: unknown) => {
          if (!controller.signal.aborted) {
            setResponse(null);
            setError(getVesselSearcherErrorMessage(requestError));
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsLoading(false);
          }
        });
    }, delay);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [activeRangeFilters, filters, offset, sortBy, sortDirection, useMockData]);

  const setFilters = useCallback((nextFilters: VesselSearcherFilters) => {
    const changedRanges = changedRangeFilters(filters, nextFilters);
    if (changedRanges.length > 0) {
      setActiveRangeFilters((current) => Array.from(new Set([...current, ...changedRanges])));
    }

    setFiltersState(nextFilters);
    setOffset(0);
  }, [filters]);

  const clearFilters = useCallback(() => {
    setFiltersState(filtersFromBounds(toEffectiveBounds(response?.filterBounds)));
    setActiveRangeFilters([]);
    setOffset(0);
  }, [response?.filterBounds]);

  const setSort = useCallback((nextSortBy: VesselSearcherSortKey) => {
    setSortDirection((currentDirection) =>
      nextSortBy === sortBy && currentDirection === "asc" ? "desc" : "asc",
    );
    setSortBy(nextSortBy);
    setOffset(0);
  }, [sortBy]);

  return {
    filters,
    setFilters,
    clearFilters,
    response,
    isLoading,
    error,
    useMockData,
    activeRangeFilters,
    offset,
    pageSize: VESSEL_SEARCHER_PAGE_SIZE,
    sortBy,
    sortDirection,
    setSort,
    setOffset,
  };
};
